import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Mail, Trash2, KeyRound, Ban, CheckCircle2, Copy, ShieldCheck, Search, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_META = {
    super_admin: { label: "Super Admin", color: "#0A0A0A", desc: "Full control · invite users · manage everything" },
    sales: { label: "Sales · Executive", color: "#16A34A", desc: "View all systems · send performance reports" },
    customer: { label: "Customer", color: "#71717A", desc: "View-only access to their own plants" },
};

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [q, setQ] = useState("");

    const load = () => api.get("/admin/users").then(r => setUsers(r.data));
    useEffect(() => {
        // Load users FIRST so the table renders quickly. Plants (which
        // powers the "Access → Station Names" column for customers) is
        // fetched in parallel but never blocks the table — station IDs
        // fall back to `#12345` labels until the plants list arrives.
        load().catch(err => toast.error(formatErr(err.response?.data?.detail) || "Failed to load users"))
              .finally(() => setLoading(false));
        api.get("/plants").then(r => setPlants(r.data)).catch(() => { /* silent */ });
    }, []);

    const patch = async (id, updates) => {
        try {
            await api.patch(`/admin/users/${id}`, updates);
            toast.success("User updated");
            load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const resetPw = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
            navigator.clipboard.writeText(data.temporary_password);
            toast.success(`New password copied to clipboard: ${data.temporary_password}`, { duration: 12000 });
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const del = async (u) => {
        if (!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        }
    };

    const filtered = users.filter(u =>
        !q || u.email.toLowerCase().includes(q.toLowerCase()) || u.name.toLowerCase().includes(q.toLowerCase()) || u.role.includes(q.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Admin Console</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
                        <ShieldCheck className="w-6 h-6 text-[#0A0A0A]" /> User Management
                    </h1>
                    <p className="text-sm text-zinc-500 mt-1">Invite team members and customers. Assign role-based access to the SolarOS fleet.</p>
                </div>
                <Button onClick={() => setInviteOpen(true)} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10" data-testid="open-invite-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite User
                </Button>
            </div>

            {/* Role explainer */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(ROLE_META).map(([k, v]) => {
                    const count = users.filter(u => u.role === k).length;
                    return (
                        <div key={k} className="bg-white border border-zinc-200 rounded-sm p-4 flex items-start gap-3" data-testid={`role-card-${k}`}>
                            <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${v.color}18`, border: `1px solid ${v.color}44` }}>
                                <span className="text-lg font-mono font-bold" style={{ color: v.color }}>{count}</span>
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-zinc-900">{v.label}</div>
                                <div className="text-xs text-zinc-500 mt-0.5">{v.desc}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users…" className="pl-9 rounded-sm bg-white" data-testid="users-search" />
            </div>

            {loading ? <div className="text-sm text-zinc-500">Loading users…</div> : (
                <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]" data-testid="users-table">
                        <thead>
                            <tr className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                                <th className="py-2.5 px-4 text-left font-semibold">User</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Role</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Access</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Invited</th>
                                <th className="py-2.5 px-4 text-right font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u, i) => {
                                const rm = ROLE_META[u.role] || {};
                                const isMe = u.id === me?.id;
                                const stationNames = u.role === "customer"
                                    ? (u.allowed_station_ids || []).map(id => plants.find(p => p.id === id)?.name || `#${id}`).join(", ")
                                    : "All plants";
                                return (
                                    <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`user-row-${i}`}>
                                        <td className="py-3 px-4">
                                            <div className="font-medium text-zinc-900">{u.name} {isMe && <span className="text-[10px] font-mono text-[#16A34A] uppercase tracking-widest ml-1">· you</span>}</div>
                                            <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: `${rm.color}18`, color: rm.color, border: `1px solid ${rm.color}44` }}>
                                                <span className="w-1 h-1 rounded-full" style={{ background: rm.color }} />
                                                {rm.label}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-xs text-zinc-600 max-w-xs truncate" title={stationNames}>{stationNames}</td>
                                        <td className="py-3 px-4">
                                            {u.status === "disabled"
                                                ? <span className="text-xs font-mono text-red-600">● DISABLED</span>
                                                : <span className="text-xs font-mono text-[#16A34A]">● ACTIVE</span>}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-zinc-500 font-mono">{u.created_at?.slice(0, 10)}</td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button onClick={() => resetPw(u)} title="Reset password" className="p-1.5 text-zinc-500 hover:text-[#0A0A0A] hover:bg-zinc-100 rounded-sm transition-colors" data-testid={`reset-pw-${i}`}>
                                                    <KeyRound className="w-4 h-4" />
                                                </button>
                                                {!isMe && (
                                                    <button onClick={() => setEditUser(u)} title="Edit user" className="p-1.5 text-zinc-500 hover:text-[#16A34A] hover:bg-zinc-100 rounded-sm transition-colors" data-testid={`edit-user-${i}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {!isMe && (
                                                    <>
                                                        <button
                                                            onClick={() => patch(u.id, { status: u.status === "disabled" ? "active" : "disabled" })}
                                                            title={u.status === "disabled" ? "Enable" : "Disable"}
                                                            className="p-1.5 text-zinc-500 hover:text-[#0A0A0A] hover:bg-zinc-100 rounded-sm transition-colors" data-testid={`toggle-${i}`}
                                                        >
                                                            {u.status === "disabled" ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                                        </button>
                                                        <button onClick={() => del(u)} title="Delete user" className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" data-testid={`delete-${i}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {inviteOpen && <InviteModal plants={plants} onClose={() => setInviteOpen(false)} onCreated={load} />}
            {editUser && <EditUserModal user={editUser} plants={plants} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
        </div>
    );
}

function EditUserModal({ user, plants, onClose, onSaved }) {
    const [name, setName] = useState(user.name || "");
    const [role, setRole] = useState(user.role);
    const [stations, setStations] = useState(user.allowed_station_ids || []);
    const [saving, setSaving] = useState(false);

    const toggleStation = (id) => setStations(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { name, role };
            if (role === "customer") payload.allowed_station_ids = stations;
            else payload.allowed_station_ids = [];
            await api.patch(`/admin/users/${user.id}`, payload);
            toast.success("User updated");
            onSaved();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg rounded-sm" data-testid="edit-user-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                        <Pencil className="w-5 h-5 text-[#16A34A]" /> Edit User · {user.email}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Full Name</label>
                        <Input value={name} onChange={e => setName(e.target.value)} className="rounded-sm" data-testid="edit-name" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Role</label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger className="rounded-sm" data-testid="edit-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(ROLE_META).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
                                            <span className="font-medium">{v.label}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {role === "customer" && (
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Plant Access ({stations.length} selected)</label>
                            <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="edit-station-picker">
                                {plants.map(p => (
                                    <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                                        <Checkbox checked={stations.includes(p.id)} onCheckedChange={() => toggleStation(p.id)} data-testid={`edit-station-cb-${p.id}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-zinc-900 truncate">{p.name}</div>
                                            <div className="text-xs text-zinc-500">{p.address}</div>
                                        </div>
                                        <div className="text-xs font-mono text-zinc-600">{p.installed_capacity_kw} kWp</div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex gap-3 pt-2">
                        <Button variant="outline" onClick={onClose} className="flex-1 rounded-sm" data-testid="edit-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving || !name || (role === "customer" && stations.length === 0)} className="flex-1 bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm" data-testid="edit-save">
                            {saving ? "Saving…" : "Save Changes"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function InviteModal({ plants, onClose, onCreated }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("sales");
    const [selectedStations, setSelectedStations] = useState([]);
    const [customPw, setCustomPw] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const toggleStation = (id) => {
        setSelectedStations(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const submit = async () => {
        setLoading(true);
        try {
            const body = { email, name, role };
            if (role === "customer") body.allowed_station_ids = selectedStations;
            if (customPw.trim()) body.password = customPw.trim();
            const { data } = await api.post("/admin/users/invite", body);
            setResult(data);
            onCreated();
            toast.success("User invited");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const copyPw = () => {
        navigator.clipboard.writeText(result.temporary_password);
        toast.success("Password copied to clipboard");
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg rounded-sm" data-testid="invite-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                        <UserPlus className="w-5 h-5 text-[#0A0A0A]" /> Invite Team Member or Customer
                    </DialogTitle>
                </DialogHeader>

                {result ? (
                    <div className="space-y-4 mt-4" data-testid="invite-result">
                        <div className="p-4 bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-sm">
                            <div className="flex items-center gap-2 text-[#16A34A] font-medium text-sm mb-2">
                                <CheckCircle2 className="w-4 h-4" /> User created
                            </div>
                            <div className="text-sm text-zinc-700">
                                <span className="font-medium">{result.user.name}</span> · {result.user.email}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Temporary Password</div>
                            <div className="flex gap-2">
                                <div className="flex-1 font-mono text-sm bg-[#0A0A0B] text-[#0A0A0A] px-3 py-2.5 rounded-sm border border-zinc-800">
                                    {result.temporary_password}
                                </div>
                                <Button onClick={copyPw} variant="outline" className="rounded-sm" data-testid="copy-pw-btn">
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="text-xs text-zinc-500 mt-2">
                                Share this with the user securely — they can sign in immediately at the login page. Once email is wired, this will be sent automatically.
                            </div>
                        </div>
                        <Button onClick={onClose} className="w-full bg-[#0A0A0A] hover:bg-black text-white rounded-full" data-testid="invite-done-btn">Done</Button>
                    </div>
                ) : (
                    <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Full Name</label>
                                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Vinoth Kumar" className="rounded-sm" data-testid="invite-name" />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Email</label>
                                <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="user@example.com" className="rounded-sm" data-testid="invite-email" />
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Role</label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger className="rounded-sm" data-testid="invite-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.entries(ROLE_META).map(([k, v]) => (
                                        <SelectItem key={k} value={k} data-testid={`role-option-${k}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
                                                <span className="font-medium">{v.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-xs text-zinc-500 mt-1.5">{ROLE_META[role]?.desc}</div>
                        </div>

                        {role === "customer" && (
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Plant Access ({selectedStations.length} selected)</label>
                                <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="station-picker">
                                    {plants.map(p => (
                                        <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                                            <Checkbox
                                                checked={selectedStations.includes(p.id)}
                                                onCheckedChange={() => toggleStation(p.id)}
                                                data-testid={`station-cb-${p.id}`}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-zinc-900 truncate">{p.name}</div>
                                                <div className="text-xs text-zinc-500">{p.address}</div>
                                            </div>
                                            <div className="text-xs font-mono text-zinc-600">{p.installed_capacity_kw} kWp</div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                Password <span className="text-zinc-400 normal-case tracking-normal">(leave blank to auto-generate)</span>
                            </label>
                            <Input value={customPw} onChange={e => setCustomPw(e.target.value)} placeholder="auto-generated" className="rounded-sm font-mono" data-testid="invite-password" />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={onClose} className="flex-1 rounded-sm" data-testid="invite-cancel">Cancel</Button>
                            <Button
                                onClick={submit}
                                disabled={loading || !email || !name || (role === "customer" && selectedStations.length === 0)}
                                className="flex-1 bg-[#0A0A0A] hover:bg-black text-white rounded-full"
                                data-testid="invite-submit"
                            >
                                <Mail className="w-4 h-4 mr-2" /> {loading ? "Creating…" : "Create User"}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
